import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LangCode } from './types';
import { detectInitialLang, persistLang } from './detect';
import { ru } from './translations/ru';
import { en } from './translations/en';
import { pl } from './translations/pl';
import { de } from './translations/de';
import type { Translations } from './translations/ru';

const DICTS: Record<LangCode, Translations> = { ru, en, pl, de };

interface I18nContextValue {
  lang: LangCode;
  setLang: (lang: LangCode) => void;
  t: Translations;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<LangCode>(() => detectInitialLang());

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const value = useMemo<I18nContextValue>(() => ({
    lang,
    setLang: (next: LangCode) => {
      persistLang(next);
      setLangState(next);
    },
    t: DICTS[lang],
  }), [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

export function useT(): Translations {
  return useI18n().t;
}

export function fmt(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] !== undefined ? String(params[key]) : `{${key}}`
  );
}
